import { inject, Injectable, signal } from "@angular/core";

import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, debounceTime, map, shareReplay, startWith, switchMap, tap } from "rxjs/operators";
import { QueryController, QueryResult, QueryTermType } from "src/core/backend/controllers";
import { cacheClear$ } from "src/modules/shared/services/cache-clear.service";
import { Cacheable } from "ts-cacheable";

const initialValue: QueryResult = {
  entries: [],
};

@Injectable({ providedIn: "root" })
export class QuickSearchService {
  private readonly queryController = inject(QueryController);

  //
  readonly input$ = new BehaviorSubject<string | undefined>("");

  readonly loading = signal(true);
  readonly isEmpty$ = this.input$.pipe(map((s) => !s?.trim().length));

  private readonly maxResultCount = 10;
  private readonly debounceDelay = 200;

  results: QueryResult = initialValue;
  readonly results$: Observable<QueryResult> = this.input$.pipe(
    map((value) => value?.trim() || ""),
    tap(() => this.loading.set(true)),
    debounceTime(this.debounceDelay),
    switchMap((value) => this.execute(value)),
    tap((results) => (this.results = results)),
    catchError((error) => (console!.error(error), of({ entries: [], length: 0 } as QueryResult))),
    tap(() => this.loading.set(false)),
    startWith(this.results),
    shareReplay(1)
  );

  @Cacheable({ cacheBusterObserver: cacheClear$ })
  private execute(value: string) {
    return this.queryController.executeQuery({
      parts: [{ type: QueryTermType.Fulltext, value }],
      take: this.maxResultCount,
      withCount: false,
    });
  }
}
